import { Fragment, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { sortBy } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Button, DialogTitle, Divider, List, Stack, TextField, Typography } from '@mui/material';

import { unassignedSensors } from '@/interfaces/group';
import { addDeviceToProjectAtom, projectSensorsWithDataAtom, selectedProjectIdAtom } from '@/interfaces/project';
import ListItemWithDelete from '@/components/common/ListItemWithDelete';

import type { SyntheticEvent } from 'react';
import type { AutocompleteChangeReason } from '@mui/material';

interface ManageDevicesDialogProps {
  projectName: string;
  onClose: (reason?: string) => void;
}
interface AutocompleteItem {
  label: string;
  id: string;
}

export default function ManageDevicesDialog({ projectName, onClose }: ManageDevicesDialogProps) {
  const projectId = useAtomValue(selectedProjectIdAtom);
  const { data: sensors } = useAtomValue(unassignedSensors);
  const { data: projectSensors } = useAtomValue(projectSensorsWithDataAtom);
  const { data: isSuccess, mutate, isPending } = useAtomValue(addDeviceToProjectAtom);

  const [autocompleteList, setAutocompleteList] = useState<AutocompleteItem[]>([]);
  const [selectedSensor, setSelectedSensor] = useState<AutocompleteItem>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedSensors, setSelectedSensors] = useState<AutocompleteItem[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (sensors === undefined || projectSensors === undefined) return;

    const selectedItems: AutocompleteItem[] = projectSensors.map((sensor) => ({ id: sensor.id, label: sensor.name }));
    const autocompleteItems: AutocompleteItem[] = sensors.map((sensor) => ({ id: sensor.id, label: sensor.name }));

    setSelectedSensors(sortBy(selectedItems, ['label']));
    setAutocompleteList(sortBy(autocompleteItems, ['label']));
  }, [projectSensors, sensors]);
  useEffect(() => {
    if (isSuccess === undefined) return;
    if (isSuccess) {
      onClose('updated');
    } else {
      window.alert(t('Projects.message.cannotAddDevice'));
    }
  }, [isSuccess, onClose, t]);

  function onAutocompleteChange(_: SyntheticEvent, item: AutocompleteItem, reason: AutocompleteChangeReason) {
    if (reason !== 'selectOption') return;
    setAutocompleteList(autocompleteList.filter((sensor) => sensor.id !== item.id));
    setSelectedSensors([...selectedSensors, item]);
    setSelectedSensor(null);
    setInputValue('');
  }
  function onRemoveClick(item: AutocompleteItem) {
    setSelectedSensors(selectedSensors.filter((sensor) => sensor.id !== item.id));
    setAutocompleteList(sortBy([...autocompleteList, item], ['label']));
  }
  function onOkClick() {
    const sensorIds = selectedSensors.map((sensor) => sensor.id);
    mutate({ projectId, sensorIds });
  }

  return (
    <Stack gap={2} sx={{ minWidth: 480, p: 3 }}>
      <DialogTitle sx={{ px: 0 }}>{t('Common.label.manageDevices')}</DialogTitle>
      <Typography>{t('Projects.label.projectName')}</Typography>
      <TextField disabled value={projectName} />
      <Typography>{t('Common.label.devices')}</Typography>
      <Autocomplete
        autoHighlight
        options={autocompleteList}
        value={selectedSensor}
        inputValue={inputValue}
        onChange={onAutocompleteChange}
        onInputChange={(_, value) => setInputValue(value)}
        renderInput={(params) => <TextField {...params} />}
      />
      <List>
        <Divider />
        {selectedSensors.map((sensor) => (
          <Fragment key={sensor.id}>
            <ListItemWithDelete key={sensor.id} label={sensor.label} onRemoveClick={() => onRemoveClick(sensor)} />
          </Fragment>
        ))}
      </List>

      <Stack direction="row" justifyContent="center" gap={3}>
        <Button variant="contained" onClick={() => onClose()} sx={{ bgcolor: '#CCC', color: 'black' }}>
          {t('Common.label.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={selectedSensors.length === 0 || isPending}
          onClick={onOkClick}
          sx={{ bgcolor: 'info.main' }}
        >
          {t('Common.label.saveButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
